import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import SingleBtn from 'components/common/elements/SingleBtn';
import Name from './Name';
import Email from './Email';
import PhoneNum from './PhoneNum';
import LoginId from './LoginId';
import RegistrationStatus from './RegistrationStatus';
import SchoolYear from './SchoolYear';
import styles from 'styles/pages/mypage/MypageInfoForm.module.scss';
import classNames from 'classnames';
import { ROUTE_PATH } from 'constants/routePath';
import { MypageInfoDefaultValues } from 'constants/mypage';
import { TMypageInfoForm } from 'types/form/mypage';
import { TCourseInfo, TEditMypageInfoParams } from 'types/apis/mypage';
import { FormCheckMsgs } from 'types/components/form';
import { isTCustomAxiosError } from 'apis/config/axios';
import {
  getProfessorMypageInfo,
  getStudentMypageInfo,
  putProfessorMypageInfo,
  putStudentMypageInfo,
} from 'apis/mypage';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';
import isEqual from 'lodash.isequal';
import useModal from 'hooks/useModal';

const MypageInfoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditPage = location.pathname.includes('edit');
  const queryClient = useQueryClient();
  const userInfo = useAtomValue(userInfoAtom);

  const isStudent = userInfo?.userRole === 'STUDENT';

  const { openModal, closeModal } = useModal();

  const [dupCheckEmailMsg, setDupCheckEmailMsg] = useState<string | null>(null);
  const [dupCheckPhoneNumMsg, setDupCheckPhoneNumMsg] = useState<string | null>(
    null
  );

  const methods = useForm<TMypageInfoForm>({
    defaultValues: MypageInfoDefaultValues,
  });
  const { handleSubmit, reset, control } = methods;

  const [email, phoneNum] = useWatch({
    name: ['email', 'phoneNum'],
    control,
  });

  const onClickEdit = () => {
    navigate(ROUTE_PATH.common.MYPAGE_EDIT);
  };

  const queryKey = isStudent
    ? 'getStudentMypageInfo'
    : 'getProfessorMypageInfo';
  const fetchFunction = isStudent
    ? getStudentMypageInfo
    : getProfessorMypageInfo;

  const { data: getMypageInfoData } = useQuery(queryKey, fetchFunction, {
    onSuccess: ({ data: mypageInfo }) => {
      if (mypageInfo) {
        const formValuesFromData: TMypageInfoForm = {
          name: mypageInfo.name,
          loginId: mypageInfo.loginId,
          email: mypageInfo.email,
          phoneNum: mypageInfo.phoneNum,
          registrationStatus: mypageInfo.registrationStatus,
          schoolYear: mypageInfo.schoolYear,
        };
        reset(formValuesFromData);
      }
    },
    enabled: !!userInfo,
  });

  const mutationFunction = isStudent
    ? putStudentMypageInfo
    : putProfessorMypageInfo;

  const { mutate: putMypageInfoMutation } = useMutation(mutationFunction, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      navigate(ROUTE_PATH.common.MYPAGE);
    },
    onError: (error) => {
      if (isTCustomAxiosError(error)) {
        if (error.code === 409) {
          closeModal();
          navigate(ROUTE_PATH.common.MYPAGE);
        }
      }
    },
  });

  const handleNavigation = () => {
    const isDataEqual = isEqual(
      {
        email: getMypageInfoData?.data.email,
        phoneNum: getMypageInfoData?.data.phoneNum,
      },
      { email, phoneNum }
    );

    if (!isDataEqual) {
      openModal({
        isConfirm: true,
        title: '해당 페이지에서 나가시겠습니까?',
        text: '해당 페이지에서 나가면 작성 중인 내용이 저장되지 않습니다. 작성을 종료하시겠습니까?',
        onSubmit: () => {
          navigate(ROUTE_PATH.common.MYPAGE);
          reset();
        },
      });
      return;
    }

    navigate(ROUTE_PATH.common.MYPAGE);
  };

  const onValid = (data: TMypageInfoForm) => {
    const isEmailChanged = data.email !== getMypageInfoData?.data?.email;
    const isPhoneNumChanged =
      data.phoneNum && data.phoneNum !== getMypageInfoData?.data?.phoneNum;

    if (isEmailChanged && dupCheckEmailMsg !== FormCheckMsgs.AvailableEmail) {
      setDupCheckEmailMsg(FormCheckMsgs.NeedToDuplicatedCheck);
      return;
    } else if (
      isPhoneNumChanged &&
      dupCheckPhoneNumMsg !== FormCheckMsgs.AvailablePhoneNum
    ) {
      setDupCheckPhoneNumMsg(FormCheckMsgs.NeedToDuplicatedCheck);
      return;
    }

    const myInfoParams: TEditMypageInfoParams = {
      email: data.email,
      phoneNum: data.phoneNum || null,
    };

    openModal({
      isConfirm: true,
      title: '수정 완료',
      text: '작성하신 수정 사항을 저장하시겠습니까?',
      onSubmit: () => {
        if (isEmailChanged || isPhoneNumChanged) {
          putMypageInfoMutation(myInfoParams);
        } else {
          navigate(ROUTE_PATH.common.MYPAGE);
        }
      },
    });
  };

  useEffect(() => {
    // 뒤로가기 시 경고창
    const handlePopstate = (e: BeforeUnloadEvent) => {
      const mypageDataEqual = isEqual(
        {
          email: getMypageInfoData?.data.email,
          phoneNum: getMypageInfoData?.data.phoneNum,
        },
        { email, phoneNum }
      );

      if (!mypageDataEqual) {
        e.preventDefault();
        openModal({
          isConfirm: true,
          title: '해당 페이지에서 나가시겠습니까?',
          text: '해당 페이지에서 나가면 작성 중인 내용이 저장되지 않습니다. 작성을 종료하시겠습니까?',
          onSubmit: () => {
            navigate(ROUTE_PATH.common.MYPAGE);
            reset();
          },
        });
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onValid)}>
        {userInfo && (
          <>
            <div className={styles.label_wrapper}>
              <Name />
              <LoginId role={userInfo?.userRole} />
            </div>
            <div className={styles.label_wrapper}>
              <div className="w100">
                <Email
                  disabled={!isEditPage}
                  checkAvailability={isEditPage}
                  email={getMypageInfoData?.data.email}
                  dupCheckEmailMsg={dupCheckEmailMsg}
                  setDupCheckEmailMsg={setDupCheckEmailMsg}
                />
              </div>
              <div className="w100">
                <PhoneNum
                  disabled={!isEditPage}
                  checkAvailability={isEditPage}
                  placeholderText={
                    isEditPage ? "'-'를 제외한 연락처를 입력해 주세요." : ''
                  }
                  phoneNum={getMypageInfoData?.data.phoneNum}
                  dupCheckPhoneNumMsg={dupCheckPhoneNumMsg}
                  setDupCheckPhoneNumMsg={setDupCheckPhoneNumMsg}
                />
              </div>
            </div>
            {userInfo.userRole === 'STUDENT' && (
              <div className={styles.label_wrapper}>
                <div className="w100">
                  <RegistrationStatus disabled={true} />
                </div>
                <div className="w100">
                  <SchoolYear disabled={true} />
                </div>
              </div>
            )}
            {userInfo.userRole === 'STUDENT' ? (
              <div className={styles.label_wrapper}>
                <div className="w100">
                  <label
                    htmlFor="name"
                    className={classNames(styles.label, 'opacity_50')}
                  >
                    수강 강의
                  </label>
                  <div
                    className={classNames(styles.subject_wrapper, 'opacity_50')}
                  >
                    {getMypageInfoData &&
                      getMypageInfoData.data.studentToCourseList?.map(
                        (subject: TCourseInfo, index: number) => (
                          <div
                            key={index}
                            className={classNames(styles.subject)}
                          >
                            {subject.courseName}{' '}
                            <span className="ml16 mr16">|</span>{' '}
                            {subject.professorName}
                          </div>
                        )
                      )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt18">
                <label
                  htmlFor="name"
                  className={classNames(styles.label, 'opacity_50')}
                >
                  담당 강의
                  <span className="font_red"> *</span>
                </label>
                <div
                  className={classNames(styles.subject_wrapper, 'opacity_50')}
                >
                  {getMypageInfoData &&
                    getMypageInfoData.data.courseNameList?.map(
                      (subject, index) => (
                        <div key={index} className={classNames(styles.subject)}>
                          {subject}
                        </div>
                      )
                    )}
                </div>
              </div>
            )}
          </>
        )}
        <div className={`flex justify_center ${isEditPage && 'gap16'}`}>
          {!isEditPage ? (
            <SingleBtn
              btnText="수정"
              isBtnActive={true}
              btnStyle={{
                marginTop: '48px',
                fontSize: '14px',
              }}
              onClick={onClickEdit}
            />
          ) : (
            <>
              <SingleBtn
                type="button"
                btnText="취소"
                btnStyle={{
                  marginTop: '48px',
                  fontSize: '14px',
                }}
                onClick={handleNavigation}
              />
              <SingleBtn
                type="submit"
                btnText="저장"
                isBtnActive={true}
                btnStyle={{
                  marginTop: '48px',
                  fontSize: '14px',
                }}
              />
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default MypageInfoForm;
